import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { CandidateService } from '../candidate.service';
import { Subscription } from 'rxjs';
import { StorageMap } from '@ngx-pwa/local-storage';
import { applicationMessages, toasterMessages } from '@messages/messages';

export enum questionTypes {
  'MULTIPLE_CHOICE' = 'Multiple Choice Questions',
  'OPEN_ENDED' = 'Open Ended Questions',
  'CODING' = "Coding Questions"
}

export enum questionCategoryStatus {
  'COMPLETED' = 'Done',
  'IN_PROGRESS' = 'In progress',
  'YET_TO_START' = "Begin"
}

@Component({
  selector: 'app-assessment-categories',
  templateUrl: './assessment-categories.component.html',
  styleUrls: ['./assessment-categories.component.scss']
})

export class AssessmentCategoriesComponent implements OnInit {
  assessmentInfo: any = {
    candidateName: "",
    duration: null,
    email: "",
    name: "",
  };
  questionType = questionTypes;
  questionCategoryStatus = questionCategoryStatus;
  assessmentId: string;
  categories: any = [];
  private subscriptions: Subscription[] = [];
  isEnterpriseUser: any = false;
  submitedQuestionType;


  constructor(
    private candidateService: CandidateService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    public dialog: MatDialog,
    private storage: StorageMap) {
    this.route.queryParams.subscribe(params => {
      this.assessmentId = params?.id;
      this.isEnterpriseUser = params?.isEnterpriseUser;
      this.submitedQuestionType = params?.questionType;
    });
  }

  ngOnInit(): void {
    if (this.isEnterpriseUser === 'true') {
      this.storage.get('welcomeViewData').subscribe((assessmentInfo: any) => {
        if (this.submitedQuestionType) {
          let catIndex = assessmentInfo.categories.findIndex(item => item.questionyTpe == this.submitedQuestionType);
          if (catIndex >= 0) {
            assessmentInfo.categories[catIndex] = {
              ...assessmentInfo.categories[catIndex], ...{ categoryStatus: 'COMPLETED' }
            }
            this.storage.set('welcomeViewData', assessmentInfo).subscribe(() => { });
          }
        }
        if (assessmentInfo) {
          this.categories = assessmentInfo.categories;
          this.assessmentInfo = {
            candidateName: assessmentInfo.candidateName,
            email: assessmentInfo.email,
            name: assessmentInfo?.assessments[0]?.name,
            duration: assessmentInfo?.assessments[0]?.duration,
            welcomeTemplate: assessmentInfo?.assessments[0]?.welcomeTemplate
          };
        }
      });
    } else {
      this.subscriptions.push(
        this.candidateService.getGroupedQuestions(this.assessmentId)
          .subscribe((res: any) => {
            this.categories = res.categories;
            this.assessmentInfo = {
              candidateName: res.candidateName,
              duration: res.duration,
              email: res.email,
              name: res.name
            };
          }, (err) => {
            this.toastr.error(err?.error?.errors[0]?.error, 'Error');
          })
      );
    }

  }

  beginAssessment(questionType: string, duration: number): void {
    this.router.navigate(['candidate/exam-question-view'], {
      queryParams: { id: this.assessmentId, type: questionType, isEnterpriseUser: this.isEnterpriseUser, questionTypeDuration: duration, totalModules: this.categories?.length }
    });
  }

  get disableSubmit(): boolean {
    let sumbmitedCategories = this.categories.filter(item => item.categoryStatus === 'COMPLETED');
    return sumbmitedCategories.length === 0;
  }

  submitAssessment(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'delete-dialog-container',
      data: { danger: false, messageBody: applicationMessages.submitAssessmentConfirm }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        if (this.isEnterpriseUser === 'true') {
          this.toastr.success(toasterMessages.submitAssessmentSuccess);
          this.router.navigate(['candidate/submitted']);
        } else {
          this.candidateService.submitAssessment('', this.assessmentId)
            .subscribe((res: any) => {
              this.toastr.success(toasterMessages.submitAssessmentSuccess);
              this.router.navigate(['candidate/submitted']);
            }, (err) => {
              this.toastr.error(err?.error?.errors[0]?.error, 'Error');
            });
        }

      }
    });
  }

  getCategoryIcon(questionType: string): string {
    switch (questionType) {
      case 'MULTIPLE_CHOICE':
        return 'radio_button_checked';
      case 'OPEN_ENDED':
        return 'edit';
      case 'CODING':
        return 'code';
      default:
        return 'quiz';
    }
  }

  getCategoryIconClass(category: any): string {
    switch (category?.categoryStatus) {
      case 'COMPLETED':
        return 'icon-completed';
      case 'IN_PROGRESS':
        return 'icon-progress';
      default:
        return 'icon-pending';
    }
  }

  getModuleIconClass(category: any): string {
    // Determine icon class based on question type and status
    if (category?.categoryStatus === 'COMPLETED') {
      return 'icon-completed';
    }
    if (category?.categoryStatus === 'IN_PROGRESS') {
      return 'icon-progress';
    }

    switch (category?.questionType) {
      case 'MULTIPLE_CHOICE':
        return 'icon-multiple-choice';
      case 'OPEN_ENDED':
        return 'icon-open-ended';
      case 'CODING':
        return 'icon-coding';
      default:
        return 'icon-multiple-choice';
    }
  }

  getButtonClass(category: any): string {
    switch (category?.categoryStatus) {
      case 'COMPLETED':
        return 'btn-done';
      case 'IN_PROGRESS':
        return 'btn-continue';
      default:
        return 'btn-begin';
    }
  }

  getButtonColor(category: any): string {
    switch (category?.categoryStatus) {
      case 'COMPLETED':
        return '';
      case 'IN_PROGRESS':
        return 'accent';
      default:
        return 'primary';
    }
  }

  getButtonIcon(category: any): string {
    switch (category?.categoryStatus) {
      case 'COMPLETED':
        return 'check';
      case 'IN_PROGRESS':
        return 'play_arrow';
      default:
        return 'play_arrow';
    }
  }

  getCompletedCount(): number {
    return this.categories.filter(item => item.categoryStatus === 'COMPLETED').length;
  }

  getProgressPercentage(): number {
    if (!this.categories || this.categories.length === 0) return 0;
    return Math.round((this.getCompletedCount() / this.categories.length) * 100);
  }

  allModulesCompleted(): boolean {
    return this.categories.length > 0 && this.getCompletedCount() === this.categories.length;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sb => sb.unsubscribe());
  }
}
